import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  History,
  Upload,
  RefreshCw,
  FileText,
  IndianRupee,
  Mail,
  Trash2,
  Filter,
  Clock,
  Database,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [total, setTotal] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, statusRes] = await Promise.all([
        axios.get(`${API}/activity-logs`, {
          params: { action: filter === "all" ? undefined : filter, limit: 100 }
        }),
        axios.get(`${API}/system-status`)
      ]);
      setLogs(logsRes.data.logs || []);
      setTotal(logsRes.data.total || 0);
      setSystemStatus(statusRes.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const handleClearLogs = async () => {
    if (!window.confirm("Are you sure you want to clear all activity logs?")) return;
    try {
      await axios.delete(`${API}/activity-logs`);
      toast.success("Activity logs cleared");
      fetchData();
    } catch (error) {
      toast.error("Failed to clear logs");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "upload_tasks":
        return <Upload className="w-4 h-4 text-blue-500" />;
      case "upload_duration":
        return <Upload className="w-4 h-4 text-emerald-500" />;
      case "sync_duration":
        return <RefreshCw className="w-4 h-4 text-violet-500" />;
      case "apply_rates":
        return <IndianRupee className="w-4 h-4 text-amber-500" />;
      case "generate_invoice":
        return <FileText className="w-4 h-4 text-pink-500" />;
      case "send_email":
        return <Mail className="w-4 h-4 text-cyan-500" />;
      default:
        return <History className="w-4 h-4 text-slate-500" />;
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      upload_tasks: "bg-blue-100 text-blue-700",
      upload_duration: "bg-emerald-100 text-emerald-700",
      sync_duration: "bg-violet-100 text-violet-700",
      apply_rates: "bg-amber-100 text-amber-700",
      generate_invoice: "bg-pink-100 text-pink-700",
      send_email: "bg-cyan-100 text-cyan-700",
    };
    const labels = {
      upload_tasks: "Task Upload",
      upload_duration: "Duration Upload",
      sync_duration: "Data Sync",
      apply_rates: "Rate Application",
      generate_invoice: "Invoice Generated",
      send_email: "Email Sent",
    };
    return (
      <Badge className={`${colors[action] || "bg-slate-100 text-slate-700"} text-[10px] font-medium`}>
        {labels[action] || action}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="activity-log-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, system-ui" }}>
            Activity Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track all data uploads, syncs, and system activities
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-logs">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleClearLogs} className="text-red-600 hover:text-red-700" data-testid="clear-logs">
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Logs
          </Button>
        </div>
      </div>

      {/* Last Update Status Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-500">Last Task Upload</p>
              <p className="text-sm font-semibold text-slate-900">
                {formatDate(systemStatus?.last_task_upload)}
              </p>
            </div>
          </div>
          {systemStatus?.tasks_uploaded_count > 0 && (
            <p className="text-xs text-slate-400 pl-13">{systemStatus.tasks_uploaded_count} records</p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-500">Last Duration Upload</p>
              <p className="text-sm font-semibold text-slate-900">
                {formatDate(systemStatus?.last_duration_upload)}
              </p>
            </div>
          </div>
          {systemStatus?.durations_uploaded_count > 0 && (
            <p className="text-xs text-slate-400 pl-13">{systemStatus.durations_uploaded_count} records</p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-500">Last Data Sync</p>
              <p className="text-sm font-semibold text-slate-900">
                {formatDate(systemStatus?.last_sync)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-500">Last Rate Application</p>
              <p className="text-sm font-semibold text-slate-900">
                {formatDate(systemStatus?.last_rate_application)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Log Table */}
      <Card className="card">
        <CardHeader className="card-header py-3 flex flex-row items-center justify-between">
          <CardTitle className="card-title text-sm flex items-center gap-2">
            <History className="w-4 h-4 text-slate-600" />
            Activity History ({total} entries)
          </CardTitle>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                <SelectItem value="upload_tasks">Task Uploads</SelectItem>
                <SelectItem value="upload_duration">Duration Uploads</SelectItem>
                <SelectItem value="sync_duration">Data Syncs</SelectItem>
                <SelectItem value="apply_rates">Rate Applications</SelectItem>
                <SelectItem value="generate_invoice">Invoice Generation</SelectItem>
                <SelectItem value="send_email">Email Sent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length > 0 ? (
            <ScrollArea className="h-[500px]">
              {/* Grid Header */}
              <div 
                className="grid bg-slate-50 border-b border-slate-200 sticky top-0 z-10"
                style={{ 
                  gridTemplateColumns: '180px 120px 1fr 200px',
                  padding: '12px 24px'
                }}
              >
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Timestamp</div>
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Action</div>
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Description</div>
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Details</div>
              </div>
              {/* Grid Body */}
              {logs.map((log) => (
                <div 
                  key={log.id}
                  className="grid border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  style={{ 
                    gridTemplateColumns: '180px 120px 1fr 200px',
                    padding: '12px 24px',
                    alignItems: 'center'
                  }}
                >
                  <div className="text-xs text-slate-600 font-mono">
                    {formatDate(log.timestamp)}
                  </div>
                  <div className="flex items-center gap-2">
                    {getActionIcon(log.action)}
                    {getActionBadge(log.action)}
                  </div>
                  <div className="text-sm text-slate-700">{log.description}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(log.details).slice(0, 2).map(([key, value]) => (
                          <Badge key={key} className="bg-slate-100 text-slate-600 text-[9px]">
                            {key}: {typeof value === 'number' ? value.toLocaleString() : value}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </ScrollArea>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <History className="w-12 h-12 mx-auto mb-4" />
              <p className="text-lg font-medium">No activity logs found</p>
              <p className="text-sm">
                {filter !== "all" ? "Try a different filter" : "Activities will appear here when you upload data or perform actions"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLog;
